import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { Subscription, firstValueFrom } from 'rxjs';
import { billingScopes } from '../../../billing-consts';
import { GalleryInfoDialogComponent } from '../../../gallery/dialogs/gallery-info-dialog/gallery-info-dialog.component';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { sleep } from '../../../lib/sleep';
import { sortSubGalleries } from '../../../lib/sort-subgalleries';
import { GalleryStatusColorPipe } from '../../../pipes/gallery-status-color.pipe';
import { GalleryStatusIconPipe } from '../../../pipes/gallery-status-icon.pipe';
import { AccessLevelService } from '../../../services/access-level.service';
import { DialogService } from '../../../services/dialog.service';
import { GalleryUrlService } from '../../../services/gallery-url.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';

@Component({
  selector: 'app-gallery-tree',
  templateUrl: './gallery-tree.component.html',
  styleUrls: ['./gallery-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonDirective,
    RouterLink,
    TooltipDirective,
    SvgComponent,
    UpgradeBadgeComponent,
    LoadingIndicatorComponent,
    BillingScopePipe,
    GalleryTeaserUrlPipe,
    GalleryStatusIconPipe,
    GalleryStatusColorPipe,
  ],
})
export class GalleryTreeComponent implements OnInit, OnDestroy {
  private galleryService = inject(GalleryService);
  private router = inject(Router);
  private permissionService = inject(PermissionService);
  private sectionService = inject(SectionService);
  private accessLevelService = inject(AccessLevelService);
  private dialog = inject(DialogService);
  private swapGalleryInUrlService = inject(GalleryUrlService);
  private check = inject(ChangeDetectorRef);
  @Input() gallery: GalleryDto;
  @Input() clientMode: boolean;
  @Output() onAddedGallery = new EventEmitter();
  @Output() onClose = new EventEmitter();
  parentGallery: GalleryDto;
  subGalleries: GalleryDto[];
  links: { [galleryId: string]: string[] } = {};
  accessLevel: GalleryAccessLevel;
  loading = false;
  noAccess = false;
  galleryUpdateSub: Subscription;

  async ngOnInit() {
    this.galleryUpdateSub = this.galleryService.onGalleryUpdated$.subscribe(async gallery => {
      if (!gallery) return;
      [...(this.subGalleries || []), this.parentGallery].filter(Boolean).forEach(_gallery => {
        if (_gallery.id === gallery.id) {
          _gallery.status = gallery.status;
        }
      });
    });

    if (this.gallery.parent_gallery_id) {
      try {
        this.parentGallery = await firstValueFrom(this.galleryService.getGallery(this.gallery.parent_gallery_id));
      } catch {
        this.noAccess = true;
        return;
      }
    } else {
      this.parentGallery = this.gallery;
    }
    const [accessLevel, subGalleries, sections] = await Promise.all([
      this.accessLevelService.getAccessLevel(this.parentGallery),
      firstValueFrom(this.galleryService.getSubGalleries(this.parentGallery['id'])),
      firstValueFrom(this.sectionService.getSections(this.parentGallery['id'])),
    ]);
    this.accessLevel = accessLevel;
    this.subGalleries = [];

    sections
      .filter(section => section.object === 'gallery')
      .forEach(section => {
        const sectionGalleries = subGalleries.filter(gallery => gallery.section === section.id);
        this.subGalleries.push(...sortSubGalleries(sectionGalleries, section.sorting));
      });

    this.createLinks();
    this.check.detectChanges();
  }

  async addSubGallery(e: Event) {
    if (!this.permissionService.canDisplayElement(billingScopes.gallery.subGallery)) {
      return this.router.navigate(['a', 'pricing']);
    }

    e.preventDefault();
    e.stopPropagation();
    if (this.loading) return;
    this.loading = true;
    const gallery = await this.galleryService.createSubGallery(this.parentGallery);
    this.subGalleries.push(gallery);
    this.createLinks();
    this.loading = false;
    this.onAddedGallery.emit();
    this.check.detectChanges();
    await sleep(250);
    document.querySelector('app-edit-album-section')?.scrollIntoView();
  }

  openGalleryInfo(gallery: GalleryDto, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.dialog.open(GalleryInfoDialogComponent, { gallery, inGallery: true });
  }

  private createLinks() {
    this.links = {};
    this.links[this.parentGallery['id']] = this.swapGalleryInUrlService.getParentPath(this.parentGallery);
    this.subGalleries.forEach(
      gallery => (this.links[gallery.id] = this.swapGalleryInUrlService.getChildPath(this.parentGallery, gallery))
    );
  }

  ngOnDestroy(): void {
    this.galleryUpdateSub.unsubscribe();
  }
}
